import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import cx from 'classnames';

import { Button } from '@ui/components/ui/button';
import { formatDayAndMonth, formatShortDay, yearOf } from './productVersions';
import { QUARTERS, quarterOf, type Quarter } from './quarterCosts';
import { rangeIsFree, sprintLastDay, sprintsBetween, type SowSprint } from './statementsOfWork';

/** Copper is left out: the band being picked is brand orange, and a wash in that hue reads as part of it. */
const COVER_COLOURS = ['var(--chapter-7)', 'var(--chapter-6)', 'var(--chapter-10)', 'var(--chapter-3)'];

/** Watered down. Light enough to leave a struck-through number readable over it. */
const mix = (colour: string, strength: number): string => `color-mix(in oklab, ${colour} ${strength}%, transparent)`;

const wash = (at: number, strength: number): string => mix(COVER_COLOURS[at % COVER_COLOURS.length], strength);

/** The strength a band is drawn at where it is drawn flat, which is anywhere it has an end. */
const FULL = 16;
/** How far a band with no end reaches before it is gone: six sprints, a quarter of a year. */
const FADE_OVER = 6;

/** The strength a distance along an open band, as a straight ramp to nothing. */
const rampAt = (along: number): number => Math.max(0, FULL * (1 - along / FADE_OVER));

/** A gradient across each cell, so they join into one ramp: flat washes read as separate bands. */
const bandAt = (colour: string, along: number, open: boolean): string | undefined => {
  if (!open) return mix(colour, FULL);
  if (rampAt(along) <= 0) return undefined;
  return `linear-gradient(to right, ${mix(colour, rampAt(along))}, ${mix(colour, rampAt(along + 1))})`;
};

/** The only copy of the breakpoint the second panel appears at: a test pins it to Tailwind's own `sm`. */
export const TWO_PANEL_QUERY = '(min-width: 640px)';

/** Whether the second panel is on screen: the paging arrows have to agree with what is drawn. */
const useTwoPanels = (): boolean => {
  const query = TWO_PANEL_QUERY;
  // Two unless the browser says otherwise, so a test environment with no matchMedia draws both.
  const [two, setTwo] = useState(() => !window.matchMedia || window.matchMedia(query).matches);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (): void => setTwo(mql.matches);
    mql.addEventListener('change', onChange);
    setTwo(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return two;
};

/** A booking calendar over sprints: an overlap is refused as it is drawn rather than after it is saved. */
const SprintRangePicker = ({
  sprints,
  hint,
  leftYear,
  onLeftYear,
  startId,
  endId,
  taken,
  covered = [],
  openEnded = false,
  single = false,
  onPick,
}: {
  /** Every sprint the product has, in any year. */
  sprints: SowSprint[];
  /** The line over the grid, saying what a click here means. */
  hint: string;
  /** The year in the left panel; the right holds the one after it. */
  leftYear: string;
  onLeftYear?: (year: string) => void;
  startId: string;
  /** Always empty where the pick is open-ended: there is no second end. */
  endId: string;
  /** Sprint ids that cannot be picked, because something else has them or they are out of bounds. */
  taken: string[];
  /** A cell is too small to name its band, so the legend carries the names and the band the shape. */
  covered?: { key: string; name: string; sprintIds: string[]; open?: boolean }[];
  /** One click is the whole pick, and the band runs from it to the last sprint it can reach. */
  openEnded?: boolean;
  /** One year on screen rather than two side by side. Either way the arrows page it. */
  single?: boolean;
  onPick: (startId: string, endId: string) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  // Pointer only: the keyboard asks the same question by moving the focus.
  const [hovered, setHovered] = useState('');
  // An open band reaches the end whatever the pointer rests on, so the hover is not read.
  const noteHover = (id: string): void => {
    if (!openEnded) setHovered(id);
  };
  const forgetHover = (id: string): void => {
    if (!openEnded) setHovered(current => (current === id ? '' : current));
  };

  const years = [...new Set(sprints.map(sprint => yearOf(sprint.startDate)))].sort();
  const firstYear = years[0] ?? leftYear;
  const lastYear = years[years.length - 1] ?? leftYear;
  // One panel below the sm breakpoint, so what the left panel may hold depends on what is drawn.
  const twoPanels = useTwoPanels() && !single;
  /** The last year the left panel can hold: one short of the last, where a second panel holds that. */
  // Not below the first year a sprint is in, or a product with one year of sprints opens on an empty one.
  const lastLeft = twoPanels && lastYear > firstYear ? String(Number(lastYear) - 1) : lastYear;
  // Held to what the left panel can hold: opened past it, the arrows hide and the earlier years go.
  const shownYear = leftYear > lastLeft ? lastLeft : leftYear;
  // Said out loud, or the caller holds a year the grid is not drawing and pages from the wrong one.
  useEffect(() => {
    if (shownYear !== leftYear) onLeftYear?.(shownYear);
  }, [shownYear, leftYear]);
  /** Keeps each band's place in `covered`, or the legend names colours that are not on the grid. */
  const legend = covered
    .map((band, at) => ({ band, at }))
    .filter(({ band }) =>
      band.sprintIds.some((id, along) => {
        const sprint = sprints.find(each => each.id === id);
        if (!sprint || (band.open && rampAt(along) <= 0)) return false;
        const year = yearOf(sprint.startDate);
        return year === shownYear || (twoPanels && year === String(Number(shownYear) + 1));
      }),
    );

  const pages = firstYear < lastLeft;
  const step = (by: number): void => {
    const next = String(Number(shownYear) + by);
    if (next >= firstYear && next <= lastYear) onLeftYear?.(next);
  };

  const inYear = (year: string): SowSprint[] =>
    sprints.filter(sprint => yearOf(sprint.startDate) === year).sort((a, b) => a.startDate.localeCompare(b.startDate));

  const dayOf = (id: string): string => sprints.find(sprint => sprint.id === id)?.startDate ?? '';
  /** The end being drawn to, and only forwards: before the held start is where a new band begins. */
  const drawingTo = openEnded ? '' : endId || (startId && hovered && dayOf(hovered) > dayOf(startId) ? hovered : '');
  /** No preview: an open band always reaches the end, and one drawn from the opening focus read as already picked. */
  const openFrom = openEnded ? startId : '';
  /** What takes over is the first sprint out of bounds. */
  const runFrom = (fromId: string): string[] => {
    const ordered = [...sprints].sort((a, b) => a.startDate.localeCompare(b.startDate));
    const at = ordered.findIndex(sprint => sprint.id === fromId);
    if (at < 0) return [];
    const free = ordered.slice(at).findIndex(sprint => taken.includes(sprint.id));
    return ordered.slice(at, free < 0 ? undefined : at + free).map(sprint => sprint.id);
  };
  /** The sprints the band covers: the run on from one end, or everything between two. */
  const spanBetween = (): string[] =>
    startId && drawingTo ? sprintsBetween(sprints, startId, drawingTo).map(sprint => sprint.id) : [];
  const span = openEnded ? runFrom(openFrom) : spanBetween();
  // Drawn as refused rather than not drawn: one that stops following the pointer reads as broken.
  const spanIsFree = openEnded || !startId || !drawingTo || rangeIsFree(sprints, taken, startId, drawingTo);
  const isPickingEnd = !openEnded && Boolean(startId) && !endId;
  /** Once closed, only the closing cell keeps the later day, so the grid cannot disagree with the summary. */
  const namesLastDay = (sprint: SowSprint): boolean => isPickingEnd || (sprint.id === endId && sprint.id !== startId);

  /** A click before the start or across something taken begins a new span rather than being refused. */
  const pick = (sprint: SowSprint): void => {
    if (taken.includes(sprint.id)) return;
    // Clicking the sprint it starts on gives it up, as clicking an end of a closed span does.
    if (openEnded) {
      onPick(sprint.id === startId ? '' : sprint.id, '');
      return;
    }
    // Clicking either end of a closed span gives it up: it is the only way back to nothing picked.
    if (endId && (sprint.id === startId || sprint.id === endId)) {
      onPick('', '');
      return;
    }
    const startsFresh =
      !startId || endId || sprint.startDate < (sprints.find(each => each.id === startId)?.startDate ?? '');
    if (startsFresh) {
      onPick(sprint.id, '');
      return;
    }
    if (!rangeIsFree(sprints, taken, startId, sprint.id)) {
      onPick(sprint.id, '');
      return;
    }
    onPick(startId, sprint.id);
  };

  const panel = (year: string): JSX.Element => {
    const held = inYear(year);
    return (
      /** Fixed at seven columns, so paging a year moves nothing and the arrows stay beside the grids. */
      // Keyed by the year, so stepping the arrows remounts the panel and it fades rather than jumps.
      <div key={year} className="w-[23rem] shrink-0 animate-in fade-in duration-[180ms] motion-reduce:animate-none">
        <p className="pb-2 text-center text-sm font-semibold text-foreground">{year}</p>
        {/* Four quarters' worth of height however many the year holds, so paging never resizes it. */}
        <div className="min-h-[12rem]">
          {!held.length ? (
            <p className="py-6 text-center text-xs text-muted-foreground">{t('sprintPicker.noSprintsInYear')}</p>
          ) : (
            QUARTERS.map((quarter: Quarter) => {
              const row = held.filter(sprint => quarterOf(sprint.startDate) === quarter);
              if (!row.length) return null;
              return (
                <div key={quarter} className="flex items-center gap-1 py-0.5">
                  {/* The gutter, where a calendar keeps its week numbers. */}
                  <span className="w-6 shrink-0 text-right text-[11px] font-medium text-muted-foreground">
                    {t(`budget.quarters.${quarter}`)}
                  </span>
                  {/* No gaps between cells: the span is one band, the way a booked week is. */}
                  <div className="flex min-w-0">
                    {row.map(sprint => {
                      const isTaken = taken.includes(sprint.id);
                      const inSpan = span.includes(sprint.id);
                      // The hover asks "does this one finish it", and the circle is what answers.
                      const isEnd = openEnded
                        ? sprint.id === openFrom
                        : sprint.id === startId || sprint.id === endId || sprint.id === drawingTo;
                      const opensBand = inSpan && span[0] === sprint.id;
                      // An open-ended band is never closed off: the square edge is what says it runs on.
                      const closesBand = !openEnded && inSpan && span[span.length - 1] === sprint.id;
                      /** The picked span wins outright: two Tailwind backgrounds are settled by emit order. */
                      const under = covered.findIndex(band => band.sprintIds.includes(sprint.id));
                      const coveredBy = covered[under];
                      const opensCover = coveredBy?.sprintIds[0] === sprint.id;
                      // A band that runs on has faded to nothing, so there is no far corner to round.
                      const closesCover =
                        !coveredBy?.open && coveredBy?.sprintIds[coveredBy.sprintIds.length - 1] === sprint.id;
                      const spanTint = spanIsFree ? 'bg-primary/15' : 'bg-destructive/15';
                      /** Mixed rather than classed: both fade along a band, and emit order would decide otherwise. */
                      const tint = ((): string | undefined => {
                        if (inSpan && openEnded) return bandAt('var(--primary)', span.indexOf(sprint.id), true);
                        if (inSpan || !coveredBy) return undefined;
                        return bandAt(
                          COVER_COLOURS[under % COVER_COLOURS.length],
                          coveredBy.sprintIds.indexOf(sprint.id),
                          !!coveredBy.open,
                        );
                      })();
                      return (
                        <button
                          key={sprint.id}
                          type="button"
                          disabled={isTaken}
                          // What is held, not hovered: a resting pointer is not a choice anyone has made.
                          aria-pressed={sprint.id === startId || sprint.id === endId}
                          // The days it covers, for anyone who wants the answer the cell is too small for.
                          title={t('sprintPicker.span', {
                            from: formatShortDay(sprint.startDate),
                            to: formatShortDay(sprintLastDay(sprints, sprint)),
                          })}
                          aria-label={t('sprintPicker.sprintCell', {
                            number: sprint.number,
                            year,
                            from: formatShortDay(sprint.startDate),
                            to: formatShortDay(sprintLastDay(sprints, sprint)),
                          })}
                          // A colour rather than a class, since the strength varies along the band.
                          style={tint ? { background: tint } : undefined}
                          onClick={() => pick(sprint)}
                          onMouseEnter={() => noteHover(sprint.id)}
                          onMouseLeave={() => forgetHover(sprint.id)}
                          onFocus={() => noteHover(sprint.id)}
                          onBlur={() => forgetHover(sprint.id)}
                          className={cx(
                            'group/cell flex h-11 w-12 shrink-0 items-center justify-center outline-none transition-colors',
                            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                            isTaken ? 'cursor-not-allowed' : 'cursor-pointer',
                            // Drawn on the cell so the band runs unbroken across the row.
                            inSpan && !openEnded && spanTint,
                            (inSpan ? opensBand : opensCover) && 'rounded-l-md',
                            (inSpan ? closesBand : closesCover) && 'rounded-r-md',
                          )}
                        >
                          {/* The date under the number: "sprint 16" only means something as the day it starts. */}
                          <span className="flex size-full flex-col items-center justify-center gap-px">
                            <span
                              // No scale: on a 24px disc the number scaled with it, which read as a flinch.
                              className={cx(
                                'flex size-6 items-center justify-center rounded-full text-xs tabular-nums',
                                // Lands under the click rather than fading: a fade read as the circle blinking.
                                openEnded && isEnd ? 'transition-none' : 'transition-colors',
                                isTaken && 'text-muted-foreground/60 line-through',
                                !isTaken &&
                                  isEnd &&
                                  (spanIsFree
                                    ? 'bg-primary font-semibold text-primary-foreground'
                                    : 'bg-destructive font-semibold text-destructive-foreground'),
                                !isTaken && !isEnd && 'font-medium text-foreground group-hover/cell:bg-muted',
                              )}
                            >
                              {sprint.number}
                            </span>
                            <span
                              className={cx(
                                'whitespace-nowrap text-[10px] leading-tight tabular-nums',
                                isTaken ? 'text-muted-foreground/60 line-through' : 'text-muted-foreground',
                              )}
                            >
                              {formatDayAndMonth(
                                namesLastDay(sprint) ? sprintLastDay(sprints, sprint) : sprint.startDate,
                              )}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // Spans the dialog, so its edge lines up with the fields under it.
  return (
    <div className="rounded-xl border border-border bg-popover p-3">
      <div className="mx-auto flex w-fit min-w-full items-center justify-between gap-2 pb-1">
        {/* Only where there is another year to reach: one year of sprints is one panel. */}
        {pages && (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={t('sprintPicker.earlierYears')}
            disabled={shownYear <= firstYear}
            onClick={() => step(-1)}
          >
            <ChevronLeft className="size-4" />
          </Button>
        )}
        {/* The instruction only: what has been picked is written out under the grid. */}
        <p className="flex-1 text-center text-xs text-muted-foreground">{hint}</p>
        {pages && (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={t('sprintPicker.laterYears')}
            disabled={shownYear >= lastLeft}
            onClick={() => step(1)}
          >
            <ChevronRight className="size-4" />
          </Button>
        )}
      </div>
      {/* Two years at once where there is room, and the left one alone where there is not. */}
      <div className="mx-auto flex w-fit gap-5">
        {panel(shownYear)}
        {twoPanels && <div className="border-l border-border pl-5">{panel(String(Number(shownYear) + 1))}</div>}
      </div>
      {/* Whose the washed bands are: a swatch and a name, no dates. The grid already shows those. */}
      {!!legend.length && (
        // Keyed by the year, so the names fade out with the bands they name.
        <div
          key={shownYear}
          className="mx-auto flex w-fit flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-2 text-[11px] animate-in fade-in duration-[180ms] motion-reduce:animate-none"
        >
          {legend.map(({ band, at }) => (
            <span key={band.key} className="flex items-center gap-1.5">
              {/* Stronger than the wash it stands for: at ten pixels across, the wash itself is nothing. */}
              <span className="size-2.5 shrink-0 rounded-sm" style={{ backgroundColor: wash(at, 60) }} />
              <span className="font-medium text-foreground">{band.name}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default SprintRangePicker;
